// Static Arabic/English content for the Education & Study sections.
// Kept on the frontend (no DB) since it is purely informational.

export const EDU_SECTIONS = [
  { key: "start",        icon: "BookOpen",     color: "purple",  ar: "بداية الدراسة في أمريكا",   en: "Starting Your Education" },
  { key: "directory",    icon: "GraduationCap", color: "sky",    ar: "دليل الجامعات والكليات",      en: "Colleges & Universities" },
  { key: "scholarships", icon: "Coins",        color: "amber",   ar: "المنح والمساعدات المالية",  en: "Scholarships & Aid" },
  { key: "english",      icon: "Languages",    color: "emerald", ar: "برامج اللغة الإنجليزية",    en: "English Language Programs" },
  { key: "contact",      icon: "Mail",         color: "rose",    ar: "التواصل مع الجامعات",       en: "Contacting Universities" },
  { key: "life",         icon: "Users",        color: "violet",  ar: "الحياة الطلابية",            en: "Student Life" },
];

// ────────── 📚 Starting Your Education ──────────
export const START_CONTENT = {
  blocks: [
    {
      title_ar: "الفرق بين الجامعات والكليات المجتمعية",
      title_en: "Universities vs Community Colleges",
      body_ar: [
        "**Universities (جامعات)** مؤسسات لـ 4 سنوات تمنح شهادة البكالوريوس، وكثير منها يقدّم دراسات عليا (ماجستير، دكتوراه). معايير القبول أعلى، والتكلفة أكبر.",
        "**Community Colleges (كليات مجتمعية)** مؤسسات لـ سنتين تمنح Associate Degree أو شهادات مهنية. القبول أسهل، التكلفة أقل بكثير، وكثيراً ما تتيح التحويل لجامعة 4 سنوات لاحقاً.",
        "**نموذج 2+2**: يدرس الطالب أول سنتين في Community College ثم ينتقل لجامعة لإكمال البكالوريوس — يوفر آلاف الدولارات.",
      ],
    },
    {
      title_ar: "ما هي تأشيرة F-1؟",
      title_en: "What is an F-1 Visa?",
      body_ar: [
        "F-1 تأشيرة الطلاب الأكاديميين الأشيع، تمنحها السفارة الأمريكية لمن قُبل في مؤسسة تعليمية معتمدة من SEVP.",
        "تسمح بالدراسة بدوام كامل، والعمل داخل الحرم الجامعي (≤ 20 ساعة/أسبوع) أثناء الفصل، وبدوام كامل في العطلات.",
        "تتطلب إثبات قدرة مالية، روابط بالبلد الأصلي (Strong Ties)، ومقابلة قنصلية.",
      ],
    },
    {
      title_ar: "ما هو الـ I-20؟",
      title_en: "What is the I-20?",
      body_ar: [
        "الـ I-20 وثيقة رسمية تُصدرها الجامعة بعد قبولك، وتثبت أنك مسجَّل في برنامج دراسي معتمد.",
        "هذه الوثيقة شرط أساسي للتقديم على تأشيرة F-1 ولدفع رسوم SEVIS (350$).",
        "تحتفظ بنسختك الأصلية وتقدّمها مع جواز السفر عند كل دخول إلى أمريكا.",
      ],
    },
    {
      title_ar: "هل 20K–30K$ يكفي للدراسة في أمريكا؟",
      title_en: "Is $20K–$30K Enough?",
      body_ar: [
        "الإجابة: **يعتمد على الولاية ونوع المؤسسة**.",
        "- **Community College في ولاية متوسطة التكلفة**: 20K–30K$ كافي لسنة كاملة (رسوم + سكن + معيشة).",
        "- **جامعة حكومية لطلاب من خارج الولاية**: عادة 35K–55K$ للسنة الواحدة.",
        "- **جامعة خاصة (Ivy League، Stanford)**: 70K–90K$ للسنة الواحدة.",
        "**نصيحة**: ابدأ بـ Community College أو جامعة حكومية في ولاية ذات تكلفة معيشة منخفضة (Texas, Florida, Arizona) ثم انتقل لاحقاً.",
      ],
    },
  ],
  resources: [
    { label: "Study in the States (DHS)", url: "https://studyinthestates.dhs.gov" },
    { label: "EducationUSA",              url: "https://educationusa.state.gov" },
    { label: "SEVIS I-901 Fee",           url: "https://www.fmjfee.com" },
  ],
};

// ────────── 💰 Scholarships ──────────
export const SCHOLARSHIPS_CONTENT = {
  blocks: [
    {
      title_ar: "أنواع المنح",
      title_en: "Types of Scholarships",
      body_ar: [
        "**Merit-Based** — على أساس التفوق الأكاديمي أو الرياضي أو الفني.",
        "**Need-Based** — على أساس الاحتياج المالي (نادرة للدوليين).",
        "**Athletic** — منح رياضية (NCAA Division I, II).",
        "**Departmental** — منح من كلية معينة داخل الجامعة.",
        "**External (Private)** — من مؤسسات وشركات خارج الجامعة.",
        "**Country-Specific** — منح للطلاب من دول معينة (مثل Fulbright).",
      ],
    },
    {
      title_ar: "أين تجد المنح؟",
      title_en: "Where to Find Scholarships",
      body_ar: [
        "موقع كل جامعة في قسم Financial Aid → International Students.",
        "محركات بحث مجانية: **Fastweb**، **BigFuture (College Board)**، **Scholarships.com**، **InternationalScholarships.com**.",
        "EducationUSA — مكاتب تعمل في معظم الدول العربية وتساعد مجاناً.",
        "Fulbright — منح حكومية مرموقة للطلاب العرب.",
        "مؤسسات الدولة في بلدك (وزارات التعليم، صناديق الابتعاث).",
      ],
    },
    {
      title_ar: "نصائح للتقديم",
      title_en: "Application Tips",
      body_ar: [
        "ابدأ مبكراً: **12-18 شهراً قبل بداية الدراسة**.",
        "قدّم لأكبر عدد ممكن من المنح — كل منحة فرصة منفصلة.",
        "اكتب Personal Statement مخصص لكل منحة (لا تنسخ).",
        "احصل على خطابات توصية من أساتذة يعرفونك جيداً، وأعطهم وقت كاف.",
        "راجع المتطلبات بدقة: تاريخ التقديم، GPA، شهادات لغة (TOEFL/IELTS).",
        "احذر من المنح التي تطلب رسوم تقديم — المنح الحقيقية مجانية دائماً.",
      ],
    },
  ],
  resources: [
    { label: "Fastweb",                url: "https://www.fastweb.com" },
    { label: "BigFuture",              url: "https://bigfuture.collegeboard.org" },
    { label: "Fulbright Foreign Student", url: "https://foreign.fulbrightonline.org" },
    { label: "EducationUSA",           url: "https://educationusa.state.gov" },
  ],
};

// ────────── 🗣️ English Language Programs ──────────
export const ENGLISH_CONTENT = {
  blocks: [
    {
      title_ar: "ما هو ESL؟",
      title_en: "ESL Programs",
      body_ar: [
        "ESL = English as a Second Language — برامج تعليم الإنجليزية لمن ليست لغتهم الأم.",
        "تتوفر في معظم Community Colleges والجامعات وفي مدارس لغة مستقلة.",
        "تشمل عدة مستويات (Beginner → Advanced) مع تركيز على القراءة، الكتابة، المحادثة، والاستماع.",
        "بعض البرامج تمنح Certificate يساعد لاحقاً في القبول الأكاديمي.",
      ],
    },
    {
      title_ar: "برامج اللغة المكثفة (Intensive English Programs - IEP)",
      title_en: "Intensive English Programs",
      body_ar: [
        "برامج 18-25 ساعة دراسة أسبوعياً — تركيز قوي ومستمر للوصول لمستوى أكاديمي خلال 6-12 شهر.",
        "كثير من الجامعات تقبل الطلاب على I-20 من خلال برنامج IEP الخاص بها قبل البدء بالدراسة الأكاديمية.",
        "بديل عن TOEFL/IELTS في بعض الجامعات: إكمال أعلى مستوى من IEP يُعفي من اختبار اللغة.",
        "أمثلة معروفة: ELS, Kaplan, EF, ومراكز اللغة في جامعات كبرى مثل UCLA Extension و Columbia ALP.",
      ],
    },
  ],
};

// ────────── 📧 Contacting Universities ──────────
export const CONTACT_CONTENT = {
  blocks: [
    {
      title_ar: "كيف تتواصل مع الجامعات",
      title_en: "How to Contact Universities",
      body_ar: [
        "**Admissions Office** — مسؤول عن طلبات القبول العامة وإجراءاتها.",
        "**International Student Office (ISO/ISSS)** — مسؤول عن شؤون الطلاب الدوليين: I-20، تأشيرة F-1، السكن، التوجيه.",
        "**Department of the major** — تواصل مع منسق التخصص للأسئلة الأكاديمية.",
        "استخدم بريداً إلكترونياً رسمي اللهجة (Gmail يكفي)، وضع موضوع واضح، وعرّف نفسك في أول سطر.",
      ],
    },
    {
      title_ar: "أسئلة يجب طرحها على Admissions",
      title_en: "Questions to Ask Admissions",
      body_ar: [
        "ما الحد الأدنى من GPA/TOEFL/IELTS المطلوب للقبول للسنة القادمة؟",
        "ما رسوم الدراسة للطلاب الدوليين (Out-of-State Tuition) للسنة الحالية؟",
        "ما المتطلبات المالية لإصدار I-20؟",
        "هل تقدّمون منح للطلاب الدوليين؟ ما شروطها؟",
        "ما خيارات السكن المتاحة للطلاب الدوليين السنة الأولى؟",
        "ما مدّة المعالجة المعتادة بعد اكتمال الطلب؟",
        "هل هناك Conditional Admission إن لم أحقق درجة اللغة المطلوبة؟",
      ],
    },
    {
      title_ar: "International Student Office",
      title_en: "International Student Office (ISO)",
      body_ar: [
        "هذا المكتب هو مرجعك الرئيسي بعد القبول. يتولى:",
        "- إصدار I-20 وتجديده.",
        "- إرشاد التأشيرة وSEVIS.",
        "- متابعة وضعك القانوني (Status Maintenance).",
        "- الترخيص للعمل (CPT/OPT).",
        "- الإرشاد الأكاديمي والثقافي.",
        "ابقَ على تواصل مستمر، خصوصاً عند أي تغيير في وضعك الدراسي أو السفر.",
      ],
    },
  ],
  templates: [
    {
      key: "inquiry",
      title_ar: "نموذج رسالة استفسار قبل التقديم",
      title_en: "Pre-Application Inquiry Email",
      body: `Subject: Inquiry from Prospective International Student — Fall 2026

Dear Admissions Office,

My name is [اسمك الكامل] and I am an international student from [بلدك]. I am interested in applying for the [Bachelor's/Master's] program in [التخصص] for the Fall 2026 intake.

I would appreciate clarification on the following points:
1. The minimum TOEFL/IELTS score required for admission.
2. The estimated total cost of attendance for international students.
3. Any scholarships or financial aid available to international applicants.
4. The application deadline and required supporting documents.

Thank you very much for your time and assistance.

Best regards,
[اسمك]
[بلدك]
[إيميلك]`,
    },
    {
      key: "followup",
      title_ar: "نموذج متابعة بعد التقديم",
      title_en: "Application Follow-Up Email",
      body: `Subject: Application Status — [اسمك الكامل] — Application ID [إن وجد]

Dear Admissions Team,

I hope this email finds you well. I submitted my application for the [Program] on [التاريخ] (Application ID: [الرقم]).

I am writing to kindly request an update on the status of my application. Please let me know if any additional documents are needed.

Thank you for your time and consideration.

Best regards,
[اسمك]`,
    },
  ],
};

// ────────── 🎓 Student Life ──────────
export const LIFE_CONTENT = {
  blocks: [
    {
      title_ar: "خيارات السكن",
      title_en: "Housing Options",
      body_ar: [
        "**On-Campus Housing (سكن جامعي)**: السنة الأولى عادةً. آمن، قريب من الحرم، يساعد على الاندماج. التكلفة 8K–15K$/سنة.",
        "**Off-Campus Apartments**: شقة منفردة أو مشتركة مع طلاب آخرين. أرخص في معظم المدن، يتطلب نقلاً وفواتير.",
        "**Homestay**: العيش مع عائلة أمريكية — تجربة ثقافية ولغوية ممتازة لمن يتعلم الإنجليزية.",
        "**Graduate Housing**: لطلاب الدراسات العليا، عادة في وحدات أكثر استقلالية.",
      ],
    },
    {
      title_ar: "العمل أثناء الدراسة",
      title_en: "Working While Studying",
      body_ar: [
        "تأشيرة **F-1** تسمح بـ:",
        "- **On-Campus Work**: حتى 20 ساعة/أسبوع أثناء الفصل، 40 ساعة/أسبوع في العطلات. بدون موافقة مسبقة.",
        "- **CPT (Curricular Practical Training)**: تدريب مرتبط بالمنهج، يتطلب موافقة DSO قبل البدء.",
        "- **OPT (Optional Practical Training)**: عمل بدوام كامل لمدة سنة بعد التخرج (وحتى 3 سنوات لتخصصات STEM).",
        "**ممنوع** العمل في وظائف عادية خارج الحرم بدون CPT/OPT — قد يفقدك وضعك القانوني.",
      ],
    },
    {
      title_ar: "التأمين الصحي للطلاب",
      title_en: "Health Insurance for Students",
      body_ar: [
        "**إلزامي**: معظم الجامعات تشترط تأميناً صحياً ساري المفعول كشرط للتسجيل.",
        "**خطة الجامعة (Student Health Plan)**: عادة 1500–3500$/سنة. تغطية واسعة وسهولة في التعامل.",
        "**تأمين خاص**: من شركات مثل ISO، PSI، StudentSecure — قد يكون أرخص لكن يجب التأكد من قبوله من الجامعة.",
        "**ما يجب التحقق منه**: تغطية الطوارئ، الأسنان، البصر، الأمومة، الإعادة للوطن (Medical Evacuation).",
      ],
    },
  ],
};
